import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

/**
 * Приватные закладки: пользователь видит только свои сохранённые контакты.
 * RLS не позволяет узнать, кто сохранил тебя, — публичных счётчиков нет.
 */
export function useSavedContacts() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: savedIds = new Set<string>() } = useQuery({
    queryKey: ["saved-contacts", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("saved_contacts")
        .select("saved_user_id")
        .eq("user_id", user!.id);
      if (error) throw error;
      return new Set((data ?? []).map((r) => r.saved_user_id as string));
    },
  });

  const toggle = useMutation({
    mutationFn: async (targetUserId: string) => {
      if (!user?.id || user.id === targetUserId) return;
      if (savedIds.has(targetUserId)) {
        const { error } = await supabase
          .from("saved_contacts")
          .delete()
          .eq("user_id", user.id)
          .eq("saved_user_id", targetUserId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("saved_contacts")
          .insert({ user_id: user.id, saved_user_id: targetUserId });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-contacts", user?.id] });
    },
  });

  return {
    savedIds,
    isSaved: (id: string) => savedIds.has(id),
    toggleSaved: (id: string) => toggle.mutate(id),
    isPending: toggle.isPending,
    canSave: !!user?.id,
    myId: user?.id ?? null,
  };
}

export function SaveContactButton({
  targetUserId,
  fullName,
  withLabel = false,
  className = "",
}: {
  targetUserId: string;
  fullName?: string | null;
  withLabel?: boolean;
  className?: string;
}) {
  const { isSaved, toggleSaved, canSave, myId } = useSavedContacts();
  if (!canSave || myId === targetUserId) return null;

  const saved = isSaved(targetUserId);
  const Icon = saved ? BookmarkCheck : Bookmark;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    toggleSaved(targetUserId);
  };

  if (withLabel) {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-pressed={saved}
        aria-label={saved ? "Убрать из сохранённых" : "Сохранить контакт"}
        className={`inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition ${
          saved
            ? "border-td-teal bg-td-teal/10 text-td-teal"
            : "border-td-line text-td-dark hover:border-td-teal hover:text-td-teal"
        } ${className}`}
      >
        <Icon className="h-4 w-4" />
        {saved ? "Сохранено" : "Сохранить контакт"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={saved}
      aria-label={
        saved
          ? `Убрать ${fullName ?? ""} из сохранённых`.trim()
          : `Сохранить контакт ${fullName ?? ""}`.trim()
      }
      title={saved ? "Сохранено" : "Сохранить контакт"}
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition ${
        saved
          ? "border-td-teal bg-td-teal/10 text-td-teal"
          : "border-td-line bg-white text-td-muted hover:border-td-teal hover:text-td-teal"
      } ${className}`}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
